import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Handshake, 
  Building2, 
  Users, 
  Globe, 
  Mail, 
  Phone, 
  CheckCircle,
  Send,
  Loader2,
  Shield,
  BarChart3,
  Network,
  FileText,
  Headphones,
  GraduationCap,
  Infinity
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FEATURES = [
  {
    icon: Infinity,
    title: 'Risorse Illimitate',
    description: 'Volontari, soci, eventi e documenti senza limiti'
  },
  {
    icon: Network,
    title: 'Gestione Multi-Ente',
    description: 'Coordina più organizzazioni da un\'unica dashboard'
  },
  {
    icon: BarChart3,
    title: 'Dashboard Federazione',
    description: 'Statistiche aggregate e KPI di rete'
  },
  {
    icon: FileText,
    title: 'Reportistica Aggregata',
    description: 'Report consolidati per tutte le organizzazioni affiliate'
  },
  {
    icon: Headphones,
    title: 'Account Manager Dedicato',
    description: 'Un referente personale per ogni esigenza'
  },
  {
    icon: GraduationCap,
    title: 'Formazione Inclusa',
    description: 'Sessioni di onboarding e training per il tuo team'
  }
];

const ORGANIZATION_TYPES = [
  { value: 'federazione', label: 'Federazione Nazionale' },
  { value: 'rete', label: 'Rete di Associazioni' },
  { value: 'coordinamento', label: 'Coordinamento Regionale' },
  { value: 'csv', label: 'Centro Servizi Volontariato (CSV)' },
  { value: 'altro', label: 'Altro' }
];

export default function Convenzioni() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    organization_name: '',
    organization_type: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    num_enti: '',
    num_volontari_stimati: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.organization_name || !formData.contact_email || !formData.organization_type) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/convenzioni/richiesta`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success('Richiesta inviata con successo!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore durante l\'invio');
      }
    } catch (error) {
      // Fallback: open mailto
      const subject = encodeURIComponent(`Richiesta Convenzione - ${formData.organization_name}`);
      const body = encodeURIComponent(`
Nome Organizzazione: ${formData.organization_name}
Tipo: ${formData.organization_type}
Contatto: ${formData.contact_name}
Email: ${formData.contact_email}
Telefono: ${formData.contact_phone}
Numero Enti Affiliati: ${formData.num_enti}
Volontari Stimati: ${formData.num_volontari_stimati}

Messaggio:
${formData.message}
      `);
      window.location.href = `mailto:convenzioni@assietica.it?subject=${subject}&body=${body}`;
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="convenzioni-success">
        <Card className="max-w-md text-center">
          <CardContent className="pt-8 pb-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">
              Richiesta Inviata!
            </h2>
            <p className="text-slate-600 mb-6">
              Il nostro team commerciale ti contatterà entro 48 ore lavorative per discutere le esigenze della tua organizzazione.
            </p>
            <div className="space-y-2">
              <p className="text-sm text-slate-500">
                Per urgenze puoi contattarci direttamente:
              </p>
              <a 
                href="mailto:convenzioni@assietica.it"
                className="text-[#0055BB] font-medium hover:underline"
              >
                convenzioni@assietica.it
              </a>
            </div>
            <Button 
              onClick={() => navigate('/dashboard')} 
              className="mt-6"
            >
              Torna alla Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="convenzioni-page">
      {/* Hero */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full mb-4">
          <Handshake className="w-5 h-5" />
          <span className="font-medium">Convenzioni Nazionali</span>
        </div>
        <h1 className="text-3xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Soluzioni per Reti e Federazioni
        </h1>
        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
          Pricing personalizzato per organizzazioni di secondo livello, federazioni nazionali 
          e reti di associazioni con esigenze complesse.
        </p>
      </div>

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {FEATURES.map((feature, index) => (
          <Card key={index} className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-emerald-100">
                  <feature.icon className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">{feature.title}</h3>
                  <p className="text-sm text-slate-600 mt-1">{feature.description}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Form Card */}
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-emerald-600" />
            Richiedi Informazioni
          </CardTitle>
          <CardDescription>
            Compila il form e il nostro team ti contatterà per una proposta personalizzata
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="organization_name">Nome Organizzazione *</Label>
                <Input
                  id="organization_name"
                  name="organization_name"
                  value={formData.organization_name}
                  onChange={handleChange}
                  placeholder="Es: Federazione Italiana Volontariato"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="organization_type">Tipo Organizzazione *</Label>
                <Select 
                  value={formData.organization_type}
                  onValueChange={(value) => handleSelectChange('organization_type', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {ORGANIZATION_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact_name">Nome Referente</Label>
                <Input
                  id="contact_name"
                  name="contact_name"
                  value={formData.contact_name}
                  onChange={handleChange}
                  placeholder="Mario Rossi"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="contact_email">Email *</Label>
                <Input
                  id="contact_email"
                  name="contact_email"
                  type="email"
                  value={formData.contact_email}
                  onChange={handleChange}
                  placeholder="mario@federazione.it"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact_phone">Telefono</Label>
                <Input
                  id="contact_phone"
                  name="contact_phone"
                  type="tel"
                  value={formData.contact_phone}
                  onChange={handleChange}
                  placeholder="+39 06 1234567"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="num_enti">N. Enti Affiliati</Label>
                <Input
                  id="num_enti"
                  name="num_enti"
                  type="number"
                  value={formData.num_enti}
                  onChange={handleChange}
                  placeholder="Es: 50"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="num_volontari_stimati">Volontari Stimati</Label>
                <Input
                  id="num_volontari_stimati"
                  name="num_volontari_stimati"
                  type="number"
                  value={formData.num_volontari_stimati}
                  onChange={handleChange}
                  placeholder="Es: 5000"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Messaggio / Esigenze Specifiche</Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Descrivi le esigenze della tua organizzazione, funzionalità richieste, tempistiche..."
                rows={4}
              />
            </div>

            <Button 
              type="submit" 
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Invio in corso...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Invia Richiesta
                </>
              )}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="flex flex-col gap-2 text-center text-sm text-slate-500 border-t pt-4">
          <p>
            Oppure contattaci direttamente:
          </p>
          <a 
            href="mailto:convenzioni@assietica.it"
            className="text-[#0055BB] font-medium hover:underline flex items-center justify-center gap-1"
          >
            <Mail className="w-4 h-4" />
            convenzioni@assietica.it
          </a>
        </CardFooter>
      </Card>

      {/* Trust Badges */}
      <div className="text-center py-8">
        <p className="text-sm text-slate-500 mb-4">Perché scegliere Assietica Gest per la tua rete?</p>
        <div className="flex flex-wrap justify-center gap-6">
          <div className="flex items-center gap-2 text-slate-600">
            <Shield className="w-5 h-5 text-green-500" />
            <span>Dati protetti GDPR</span>
          </div>
          <div className="flex items-center gap-2 text-slate-600">
            <Building2 className="w-5 h-5 text-blue-500" />
            <span>Made in Italy</span>
          </div>
          <div className="flex items-center gap-2 text-slate-600">
            <Users className="w-5 h-5 text-purple-500" />
            <span>Supporto dedicato</span>
          </div>
        </div>
      </div>
    </div>
  );
}
